/**
 * localStorage chokepoint for campaign-related client state.
 *
 * Why a thin wrapper? Two reasons:
 *   1. Centralises the storage key namespace (`dl.*`) so the codebase
 *      has one place to audit.
 *   2. Makes the SSR / vitest-happy-dom safety dance explicit — when
 *      `window` or `localStorage` is unavailable, all reads return the
 *      sentinel and writes are no-ops. The picker view boots correctly
 *      in tests even with no DOM storage.
 *
 * `dl.lastCampaignId` persists the chosen campaign across sign-out /
 * sign-in cycles. It is intentionally NOT cleared on sign-out —
 * restoring on the next sign-in matches the "switcher remembers"
 * assertion.
 */

const ACTIVE_CAMPAIGN_KEY = 'dl.lastCampaignId';
const LAST_ACTIVE_PREFIX = 'dl.lastActive.';
const CURSOR_PREFIX = 'dl.cursor.';

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getActiveCampaignId(): string | null {
  const s = safeStorage();
  if (!s) return null;
  try {
    const v = s.getItem(ACTIVE_CAMPAIGN_KEY);
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}

export function setActiveCampaignId(id: string): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(ACTIVE_CAMPAIGN_KEY, id);
  } catch {
    // Storage quota / privacy mode — silently degrade. The next boot
    // will fall through to the picker, which is acceptable.
  }
}

export function clearActiveCampaignId(): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.removeItem(ACTIVE_CAMPAIGN_KEY);
  } catch {
    // ignore
  }
}

/**
 * Per-campaign "last active" timestamp — used by the picker to sort
 * campaigns by recency. The Worker registry only carries `created_at`;
 * we layer a client-side recency timestamp so quick-switching feels
 * natural ("the one I touched 30s ago floats to the top").
 *
 * Stored as an ISO string so it sorts lexicographically.
 */
export function touchLastActive(campaignId: string, now: () => Date = () => new Date()): void {
  const s = safeStorage();
  if (!s) return;
  try {
    s.setItem(`${LAST_ACTIVE_PREFIX}${campaignId}`, now().toISOString());
  } catch {
    // ignore
  }
}

export function getLastActive(campaignId: string): string | null {
  const s = safeStorage();
  if (!s) return null;
  try {
    return s.getItem(`${LAST_ACTIVE_PREFIX}${campaignId}`);
  } catch {
    return null;
  }
}

/**
 * Per-campaign session cursor (the "Campaign date", `Campaign.
 * session_cursor` per 06-timeline.md §13). Persisted client-side so
 * the date the GM is working in survives a reload / re-open.
 *
 * Stored together with the calendar id the cursor was authored
 * against — a canonical day count only means something within a
 * calendar's coordinate system, so on read we ignore a persisted
 * cursor whose calendar no longer matches the active one (returns
 * null → "unset").
 *
 * Phase 1 keeps this client-local; promoting it to a server-side
 * `session_cursor` op on the Campaign is a later step (the value
 * would then sync across the GM's devices and co-GMs). The storage
 * key namespace is per-campaign so switching campaigns restores the
 * right date.
 */
export interface PersistedCursor {
  calendarId: string;
  cursor: number | null;
}

/**
 * The raw persisted cursor for a campaign — both the day count AND the
 * calendar id it was authored against — or null when nothing is stored.
 *
 * Unlike {@link getPersistedCursor}, this does NOT gate on a caller-supplied
 * calendar: at boot the active calendar is not yet known (it is derived from
 * the op-log fold), so the restore has to read back the stored calendar id
 * rather than guess one. The caller decides whether the stored calendar is
 * safe to adopt (a bundled calendar resolves immediately; a homebrew
 * `cal_*` definition only resolves once its pack op folds, so the caller
 * defers to the fold in that case).
 */
export function getPersistedCursorState(campaignId: string): PersistedCursor | null {
  const s = safeStorage();
  if (!s) return null;
  try {
    const raw = s.getItem(`${CURSOR_PREFIX}${campaignId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedCursor>;
    if (typeof parsed.calendarId !== 'string') return null;
    return { calendarId: parsed.calendarId, cursor: typeof parsed.cursor === 'number' ? parsed.cursor : null };
  } catch {
    return null;
  }
}

export function getPersistedCursor(campaignId: string, calendarId: string): number | null {
  const parsed = getPersistedCursorState(campaignId);
  if (parsed === null || parsed.calendarId !== calendarId) return null;
  return parsed.cursor;
}

export function setPersistedCursor(
  campaignId: string,
  calendarId: string,
  cursor: number | null,
): void {
  const s = safeStorage();
  if (!s) return;
  try {
    const payload: PersistedCursor = { calendarId, cursor };
    s.setItem(`${CURSOR_PREFIX}${campaignId}`, JSON.stringify(payload));
  } catch {
    // Storage quota / privacy mode — the cursor just won't persist;
    // the in-session value still works.
  }
}
